import {Box, Typography} from '@mui/material';
import React, {useState} from 'react';

import CustomButton from '../../../components/CustomButton/CustomButton';
import CustomSelectField from '../../../components/CustomFormFields/CustomSelectField/CustomSelectField';
import CustomTextField from '../../../components/CustomFormFields/CustomTextField/CustomTextField';
import {getLoggerConfiguration, updateLoggerConfiguration} from '../../../service/admin';
import {handleError} from '../../../utils/errors';

const Logging = ({setSnackBarAlert, setIsLoading}) => {
  const [formData, setFormData] = useState({
    operationType: {
      type: 'select',
      label: 'OperationType',
      value: 'GET',
      options: [
        {code: 'GET', label: 'Get'},
        {code: 'UPDATE', label: 'Update'},
      ],
      error: false,
      hide: false,
      helperText: '',
    },
    appName: {
      type: 'select',
      label: 'App',
      value: 'ois',
      options: [
        {code: 'ois', label: 'ois'},
        {code: 'moss', label: 'moss'},
        {code: 'phoenix', label: 'phoenix'},
      ],
      error: false,
      hide: false,
      helperText: '',
    },
    logLevel: {
      type: 'select',
      label: 'Log Level',
      value: '',
      options: [
        {code: '', label: ''},
        {code: 'error', label: 'error'},
        {code: 'warn', label: 'warn'},
        {code: 'info', label: 'info'},
        {code: 'http', label: 'http'},
        {code: 'verbose', label: 'verbose'},
        {code: 'debug', label: 'debug'},
        {code: 'silly', label: 'silly'},
      ],
      error: false,
      hide: false,
      helperText: '',
    },
    buffering: {
      type: 'select',
      label: 'Buffering',
      value: '',
      options: [
        {code: '', label: ''},
        {code: 'true', label: 'True'},
        {code: 'false', label: 'False'},
      ],
      error: false,
      hide: false,
      helperText: '',
    },
    bufferLimit: {
      type: 'number',
      label: 'Buffer Limit',
      value: '',
      error: false,
      hide: false,
      helperText: '',
    },
    retryLimit: {
      type: 'number',
      label: 'Retry Limit',
      value: '',
      error: false,
      hide: false,
      helperText: '',
    },
    flushInterval: {
      type: 'number',
      label: 'Flush Interval',
      value: '',
      error: false,
      hide: false,
      helperText: '',
    },
    transportLevel: {
      type: 'select',
      label: 'Transport Level',
      value: '',
      options: [
        {code: '', label: ''},
        {code: 'Console', label: 'Console'},
        {code: 'Elasticsearch', label: 'Elasticsearch'},
      ],
      error: false,
      hide: false,
      helperText: '',
    },
  });

  const setLoggerConfiguration = (data) => {
    setFormData((prevState) => ({
      ...prevState,
      logLevel: {
        ...prevState.logLevel,
        value: data?.logLevel || '',
      },
      buffering: {
        ...prevState.buffering,
        value: data?.buffering || '',
      },
      bufferLimit: {
        ...prevState.bufferLimit,
        value: data?.bufferLimit || '',
      },
      retryLimit: {
        ...prevState.retryLimit,
        value: data?.retryLimit || '',
      },
      flushInterval: {
        ...prevState.flushInterval,
        value: data?.flushInterval || '',
      },
      transportLevel: {
        ...prevState.transportLevel,
        value: data?.transportLevel || '',
      },
    }));
  };

  const resetFormDisplay = (isGetOperation) => {
    setFormData((prevState) => ({
      ...prevState,
      buffering: {
        ...prevState.buffering,
        hide: !isGetOperation,
      },
      bufferLimit: {
        ...prevState.bufferLimit,
        hide: !isGetOperation,
      },
      retryLimit: {
        ...prevState.retryLimit,
        hide: !isGetOperation,
      },
      flushInterval: {
        ...prevState.flushInterval,
        hide: !isGetOperation,
      },
    }));
  };

  const handleFormChange = (e) => {
    if (e.target.id === 'appName') {
      setLoggerConfiguration({});
    } else if (e.target.id === 'operationType') {
      resetFormDisplay(e.target.value === 'GET');
    }
    setFormData((prevFormData) => ({
      ...prevFormData,
      [e.target.id]: {
        ...prevFormData[e.target.id],
        value: e.target.value,
        error: false,
        helperText: '',
      },
    }));
  };

  const createUpdateRequestBody = () => {
    const data = {
      operationType: formData?.operationType?.value,
      appName: formData?.appName?.value,
      configuration: {},
    };
    if (formData?.logLevel?.value) {
      data.configuration.logLevel = formData.logLevel.value;
    }
    if (formData?.transportLevel?.value) {
      data.configuration.transportLevel = formData.transportLevel.value;
    }
    return data;
  };

  const handleButtonClick = async (e) => {
    try {
      setIsLoading(true);
      switch (formData.operationType.value) {
        case 'GET':
          {
            const result = await getLoggerConfiguration({
              operationType: formData.operationType.value,
              appName: formData.appName.value,
            });
            setLoggerConfiguration(result?.data || {});
            setSnackBarAlert((prevState) => ({
              ...prevState,
              isOpen: true,
              message: 'fetched',
              severity: 'success',
            }));
          }
          break;
        case 'UPDATE':
          {
            const data = createUpdateRequestBody();
            await updateLoggerConfiguration(data);
            setSnackBarAlert((prevState) => ({
              ...prevState,
              isOpen: true,
              message: 'updated',
              severity: 'success',
            }));
          }
          break;
        default:
      }
    } catch (error) {
      setSnackBarAlert((prevState) => ({
        ...prevState,
        isOpen: true,
        message: handleError(error),
        severity: 'error',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box sx={{p: 1, display: 'flex', flexDirection: 'row', flexWrap: 'wrap'}}>
        <CustomSelectField
          id="operationType"
          label={formData.operationType.label}
          options={formData.operationType.options}
          value={formData.operationType.value}
          onChange={handleFormChange}
          error={formData.operationType.error}
          helperText={formData.operationType.helperText}
        />
        <CustomSelectField
          id="appName"
          label={formData.appName.label}
          options={formData.appName.options}
          value={formData.appName.value}
          onChange={handleFormChange}
          error={formData.appName.error}
          helperText={formData.appName.helperText}
        />
      </Box>
      <Box sx={{p: 1, display: 'flex', flexDirection: 'row', flexWrap: 'wrap'}}>
        {formData &&
          Object.keys(formData)?.map((key, index) => {
            if (
              !formData[key].hide &&
              (formData[key].type === 'text' || formData[key].type === 'number') &&
              key !== 'operationType' &&
              key !== 'appName'
            ) {
              return (
                <CustomTextField
                  key={key}
                  id={key}
                  type={formData[key]?.type}
                  label={formData[key].label}
                  disabled={formData[key].disabled}
                  value={formData[key]?.value}
                  onChange={handleFormChange}
                  error={formData[key]?.error}
                  helperText={formData[key].helperText || ''}
                  multiline={formData[key]?.multiline || false}
                  minWidth={215}
                />
              );
            }
            if (
              !formData[key].hide &&
              formData[key].type === 'select' &&
              key !== 'operationType' &&
              key !== 'appName'
            ) {
              return (
                <CustomSelectField
                  key={key}
                  id={key}
                  label={formData[key].label}
                  disabled={formData[key].disabled}
                  options={formData[key].options}
                  value={formData[key]?.value}
                  onChange={handleFormChange}
                  error={formData[key]?.error}
                  helperText={formData[key].helperText || ''}
                />
              );
            }
            return null;
          })}
        <br />
      </Box>
      <Box sx={{display: 'flex', flexDirection: 'column', padding: '2rem'}}>
        {formData.operationType.value === 'UPDATE' ? (
          <Typography sx={{color: 'red'}}>
            Note: <br></br>- Updating without setting any values will reset the Logger configuration to defaults.{' '}
          </Typography>
        ) : (
          <></>
        )}
        <br />
        <CustomButton
          text={formData.operationType.options.find((option) => option.code === formData.operationType.value).label}
          onClick={handleButtonClick}
        />
      </Box>
    </>
  );
};

export default Logging;
